import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import PageHero from "@/components/PageHero";
import BookCTA from "@/components/BookCTA";
import EcoDisposalSection from "@/components/EcoDisposalSection";
import { COMPANY, EA_BADGE_URL } from "@/lib/brand";
import { usePageMeta } from "@/lib/seo";
import {
  Truck,
  Sofa,
  Refrigerator,
  Check,
  Trees,
  Heart,
  Handshake,
  Recycle,
  ShieldCheck,
  Clock,
} from "lucide-react";

const services = [
  {
    icon: Truck,
    title: "Full house clearance",
    body:
      "End-to-end clearance of an entire property — every room, loft, garage and shed. Ideal for end-of-tenancy, rentals, moving on or downsizing anywhere in Peterborough. We plan the right van and crew size so it's done in a single visit.",
  },
  {
    icon: Sofa,
    title: "Single item removal",
    body:
      "One sofa. One fridge. One mattress. If it won't fit in the car and the council can't collect for weeks, we can. Book online with a couple of photos and we'll turn up on a day that suits you.",
  },
  {
    icon: Refrigerator,
    title: "White goods & electricals",
    body:
      "We collect and dispose of fridges, freezers, washing machines, dishwashers, cookers, TVs and other electrical items from homes across Peterborough. Old electricals can't simply go to landfill, so every item is handled carefully and taken to the right place, giving you complete peace of mind that it's been dealt with properly.",
    points: [
      "All WEEE (Waste Electrical and Electronic Equipment) items are disposed of correctly at licensed facilities, with a WEEE certificate provided.",
      "Fridge and freezer disposal includes proper degassing of refrigerants, in compliance with regulations.",
      `Environment Agency registered waste carriers — Licence No. ${COMPANY.wasteCarrierLicence}.`,
    ],
  },
  {
    icon: Trees,
    title: "Garden waste clearance",
    body:
      "Green waste, hedge cuttings, old fence panels, shed contents, decking, patio slabs, BBQs — cleared and taken away. Perfect after a big garden overhaul or when a property needs tidying for sale.",
  },
  {
    icon: Heart,
    title: "Probate clearance",
    body:
      "A discreet, thorough clearance of an estate — inventoried where you need it and timed around solicitors, executors and property sale dates. Handled quietly and with care.",
  },
  {
    icon: Handshake,
    title: "Bereavement clearance",
    body:
      "A gentle, patient service for families clearing a loved one's home. Personal items and keepsakes are always set aside with you first. We work to your pace, never ours.",
  },
];

const trust = [
  { icon: ShieldCheck, label: "Fully insured" },
  { icon: Recycle, label: "Licensed waste carriers" },
  { icon: Clock, label: "Same-week slots" },
];

export default function Services() {
  usePageMeta(
    "Clearance Services Peterborough | Waites Clearances",
    "House clearance, furniture removal, garden waste and probate clearances in Peterborough. Peterborough's trusted, family run clearance team — free quotes, licensed waste carriers."
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="page-services">
      <Header />

      <PageHero
        eyebrow="Clearance services in Peterborough"
        title="Everything we clear across Peterborough, one team, one visit."
        description="From a single awkward item to an entire probate estate, we handle the heavy lifting, the disposal and the recycling for homes across Peterborough and the surrounding areas — so you don't have to."
      >
        <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
          {trust.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-2 text-sm text-slate-600">
              <Icon className="h-4 w-4 text-[#FF7F00]" /> {label}
            </span>
          ))}
        </div>
      </PageHero>

      <section className="py-20 sm:py-24" data-testid="services-list">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 space-y-5">
          {services.map(({ icon: Icon, title, body, points }, i) => (
            <div
              key={title}
              data-testid={`service-item-${i}`}
              className="group rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 hover:border-[#FFB870] hover:shadow-lg transition-all flex flex-col sm:flex-row gap-4 sm:gap-7"
            >
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center shrink-0">
                <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-[#FF7F00]" />
              </div>
              <div>
                <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                  {title}
                </h2>
                <p className="mt-2.5 text-slate-600 leading-relaxed">{body}</p>
                {points && (
                  <ul className="mt-4 space-y-2.5" data-testid={`service-item-${i}-points`}>
                    {points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2.5 text-slate-700 leading-relaxed">
                        <span className="mt-1 h-5 w-5 rounded-full bg-[#FFF3E6] border border-[#FFB870] flex items-center justify-center shrink-0">
                          <Check className="h-3 w-3 text-[#FF7F00]" strokeWidth={3} />
                        </span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}

          <div
            data-testid="services-licence-strip"
            className="rounded-2xl border border-slate-200 bg-[#FFF7EE] px-6 py-5 sm:px-7 sm:py-6 flex items-center gap-4"
          >
            <img
              src={EA_BADGE_URL}
              alt="Environment Agency Registered Waste Carrier"
              title="Environment Agency Registered Waste Carrier"
              className="h-11 w-auto object-contain shrink-0"
            />
            <div>
              <p className="font-display text-sm sm:text-base font-semibold text-slate-900 leading-snug">
                Every Peterborough clearance disposed of the right way.
              </p>
              <p className="mt-1 text-xs sm:text-sm text-slate-600 leading-relaxed">
                Environment Agency registered waste carriers — Licence No.{" "}
                <span className="font-semibold text-slate-900">{COMPANY.wasteCarrierLicence}</span>.
                We recycle, donate and reuse wherever possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <EcoDisposalSection />

      <BookCTA
        title="Not sure which service fits?"
        body="Get a free quote with a couple of photos and we'll advise you honestly — no obligation."
      />

      <SiteFooter />
    </div>
  );
}
